import { useCallback, useEffect, useMemo, useState } from "react";
import { usePayrollRoster } from "@/hooks/usePayrollRoster";
import { useYearEnd } from "@/hooks/useYearEnd";
import { useQuarterly } from "@/hooks/useQuarterly";
import { useAnnualFuta } from "@/hooks/useAnnualFuta";
import { demoApi, problemMessage } from "@/api/client";
import type {
  BusinessEntity,
  FilingCalendar,
  PayrollEmployee,
  QuarterStatus,
  Vendor1099Report,
} from "@/api/types";
import { PageHeader } from "@/components/PageHeader";
import { AreaTabs, PAYROLL_TABS } from "@/components/AreaTabs";
import { StateMessage } from "@/components/StateMessage";
import { Why } from "@/components/Why";
// ef-064-18 anchor: the "Missing tax info" list on the 1099-NEC worksheet (stub until ef-064-18).
import { MissingTaxInfo } from "@/components/MissingTaxInfo";
import { formatMoney } from "@/lib/format";
import { TAX_YEAR } from "@/pages/payroll-fixtures";
import { useStatePaidLeavePrograms } from "@/hooks/useStatePaidLeavePrograms";
import "./YearEnd.css";

/** 2026 Social Security wage base — FicaMedicareRates.ForYear, from the SSA's Oct 2025
 *  announcement. Displayed only; every figure on this screen comes from the engine. */
const SS_WAGE_BASE_LABEL = "184,500";

/** 2026 FUTA wage base, per employee — Form940Rates.ForYear. Displayed only. */
const FUTA_WAGE_BASE_LABEL = "7,000";

/** "2026-10-31" -> "31 October". Dates come from the server as plain days, with no time
 *  zone, so they are parsed at local midnight rather than as UTC instants — otherwise a
 *  due date can render as the day before west of Greenwich. */
function formatDay(iso: string): string {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString(undefined, { day: "numeric", month: "long" });
}

/**
 * Year-end federal forms — IRS Form 943 and each employee's W-2 boxes.
 *
 * A payroll SUB-screen, reached from the Payroll page, the same shape MN Paid Leave takes.
 *
 * ⚠️ EVERY FIGURE ON THIS SCREEN IS PROJECTED, and the banner below says so first, before
 * any number. Nothing in this system stores a payroll run, so there is no year-to-date to
 * total: what this shows is the annual return as it would stand if the year ran at the
 * roster's current rate. That is a genuinely useful thing for a farmer to see in August and
 * a dangerous thing to mistake for a filing, so the caveat is the first thing on the page
 * rather than a footnote under it — the same reasoning as the Payroll screen's disclosure
 * and the Reports screen's "balance-sheet reporting isn't available" panel.
 */
interface YearEndProps {
  /** Present when the shell can route — the MN Paid Leave tile is a link, not a view. */
  onNavigate?: (path: string) => void;
  /**
   * Which return to open on, when something navigated here to show a SPECIFIC one.
   *
   * The H-2A screen ends with three buttons naming where the exemption reaches paper —
   * "Form 940 — line 4, payments exempt from FUTA", "W-2s — badged, with Box 3 correctly
   * blank". All three called onNavigate("/year-end") with no target, so all three landed on
   * the 943, which is this screen's default. Two of them named a return and showed a
   * different one. Measured 2026-09-08 by clicking each and reading the visible card.
   *
   * Passed as a prop rather than through the URL because App's `normalize()` matches whole
   * paths — "/year-end#w2" falls through its switch to the dashboard — and widening routing
   * a week before the show is a bigger change than this is worth.
   */
  initialView?: "return" | "940" | "w2";
}

export function YearEnd({ onNavigate, initialView }: YearEndProps) {
  // "tenant": this screen groups by business and offers a switcher, so it needs them all.
  const roster = usePayrollRoster("tenant");

  // The tenant's businesses. A farmer typically has more than one, and WHICH business a
  // person works for decides which federal return their wages go on — the farm's annual
  // 943 or a non-farm business's quarterly 941. The entity table always carried this
  // (its own EIN, state and fiscal year, employees scoped per entity); it just had no
  // way to reach the screen.
  const [entities, setEntities] = useState<BusinessEntity[] | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    demoApi.getEntities(controller.signal).then(setEntities).catch(() => setEntities([]));
    return () => controller.abort();
  }, []);

  const [entityId, setEntityId] = useState<string | null>(null);
  const [quarter, setQuarter] = useState(3);

  /**
   * WHICH RETURN IS ON SCREEN. The page used to stack every form and let the reader
   * scroll: the 941 (or 943), then the W-2 table, then the 940 last — a card at ~984px
   * that the visual gate could not even see without its own locator-scoped shot. The
   * quarter tiles were already a period selector; this extends them into a return
   * selector so the annual forms are a click rather than a scroll.
   *
   * "return" means the headline return for this business — the quarterly 941, or the
   * annual 943 on a farm. The quarter tiles set it AND the quarter together.
   *
   * "1099" (ef-047-5) is wider than `initialView` on purpose — nothing navigates here
   * pre-targeting the 1099-NEC worksheet yet, so `initialView`'s own prop type stays
   * exactly what it was rather than widening a contract nothing outside this file uses.
   */
  type YearEndView = "return" | "940" | "w2" | "1099";
  const [view, setView] = useState<YearEndView>(initialView ?? "return");

  // Default to the business with the most employees — the farm, on this data.
  useEffect(() => {
    if (entityId || !roster.employees?.length) return;
    const counts = new Map<string, number>();
    for (const e of roster.employees) counts.set(e.entityId, (counts.get(e.entityId) ?? 0) + 1);
    const biggest = [...counts.entries()].sort((a, b) => b[1] - a[1])[0];
    if (biggest) setEntityId(biggest[0]);
  }, [roster.employees, entityId]);

  // Which businesses actually employ someone. An entity with no payroll files no return,
  // so offering it would be an empty promise.
  const employers = useMemo(() => {
    if (!entities || !roster.employees) return [];
    const withStaff = new Set(roster.employees.map((e) => e.entityId));
    return entities.filter((e) => withStaff.has(e.id));
  }, [entities, roster.employees]);

  // Grouped ONCE, not filtered per render. useYearEnd/useQuarterly key their effects on
  // the roster's identity, so returning a fresh `.filter()` array each render made every
  // render abort the in-flight request and start another — an infinite POST loop that
  // left the screen on "Projecting the year…" forever while the network tab filled with
  // 200s. Stable identities are load-bearing here, not a micro-optimisation.
  const rostersByEntity = useMemo(() => {
    const map = new Map<string, PayrollEmployee[]>();
    for (const employee of roster.employees ?? []) {
      const bucket = map.get(employee.entityId);
      if (bucket) bucket.push(employee);
      else map.set(employee.entityId, [employee]);
    }
    return map;
  }, [roster.employees]);

  const rosterFor = (id: string | null) =>
    id === null ? null : (rostersByEntity.get(id) ?? (roster.employees ? [] : null));

  const selectedRoster = entityId === null ? null : (rostersByEntity.get(entityId) ?? null);

  /**
   * WHICH STATE RETURN THIS BUSINESS FILES, asked rather than assumed.
   *
   * The tile below read "MN Paid Leave" for every business in every state, and the subtitle
   * named "the MN wage detail" the same way. Both were right by coincidence — every demo
   * entity is Minnesota — and neither could be wrong on screen until somebody set a business
   * to North Dakota. `entity.state` has been on the wire the whole time; nothing read it.
   */
  const selectedEntity =
    entityId === null ? null : (entities?.find((e) => e.id === entityId) ?? null);

  const { programs: statePrograms, status: statePlStatus } = useStatePaidLeavePrograms(TAX_YEAR);

  /**
   * The program for this business's state, or null when there is none.
   *
   * ⚠️ `undefined` means NOT KNOWN YET — still loading, or the lookup failed. A screen must
   * never say "no state return" because a fetch did not land, so the two are kept apart all
   * the way to the render. The first draft of this collapsed them and would have told a
   * Minnesota farmer they had nothing to file whenever the request timed out.
   */
  const stateProgram =
    statePlStatus !== "ready" || statePrograms === null || selectedEntity === null
      ? undefined
      : (statePrograms.find(
          (p) => p.state === (selectedEntity.state ?? "").trim().toUpperCase(),
        ) ?? null);

  // Agricultural vs not, decided by the DATA rather than the entity's name: an entity
  // employing anyone the engine treats as H-2A is a farm payroll, and a farm payroll
  // files the 943. Naming a business "Trucking" proves nothing.
  const isAgricultural = selectedRoster?.some((e) => e.ficaExemptByLaw) ?? true;

  // Projected for whichever business is selected. The 943 card below is gated on the
  // payroll being agricultural; the W-2 table is NOT, because a W-2 is annual and
  // universal — a trucking company files one per driver exactly as the farm does per
  // hand. Gating the whole projection on `isAgricultural` hid the non-farm W-2s
  // entirely, and with them the only employee in the demo whose earnings reach the
  // Social Security wage base.
  const { status, forms, error } = useYearEnd(selectedRoster);

  // When each quarter closes, when its return is due, how many paydays fall in it, and
  // which ones are already filed. Only meaningful for the quarterly filer.
  const [calendar, setCalendar] = useState<FilingCalendar | null>(null);
  useEffect(() => {
    setCalendar(null);
    if (!entityId || isAgricultural) return;
    const controller = new AbortController();
    demoApi
      .getFilingStatus(entityId, TAX_YEAR, controller.signal)
      .then(setCalendar)
      .catch(() => setCalendar(null));
    return () => controller.abort();
  }, [entityId, isAgricultural]);

  const quarterOf = (q: number): QuarterStatus | undefined =>
    calendar?.quarters.find((x) => x.quarter === q);

  // The quarter the business is actually standing in: the first one not yet filed. That
  // is the one a person needs to be told about, not whichever tab was clicked last.
  const nextDue = calendar?.quarters.find((q) => !q.filed);

  // Pay periods come from the real calendar, so Q1's 941 is legitimately larger than
  // Q2's. Falls back to the biweekly average only until the calendar arrives.
  const periodsForQuarter = quarterOf(quarter)?.payPeriods ?? null;

  /**
   * Whether the return CURRENTLY ON SCREEN has been filed — not whether anything on the
   * page has. Only the quarterly filer has filing history to contradict, and only while a
   * quarter is the selected view: the 940 and the W-2 table are projections in every case,
   * and the agricultural 943 has no filed quarters behind it.
   */
  const shownReturnIsFiled =
    view === "return" && !isAgricultural && Boolean(quarterOf(quarter)?.filedOn);

  // EVERY employer files a 940, so this is not gated on the 943/941 split the way the
  // other two are — it takes whichever business is selected.
  const futa = useAnnualFuta(selectedRoster);

  const quarterly = useQuarterly(
    isAgricultural ? null : selectedRoster,
    quarter,
    periodsForQuarter,
  );

  /**
   * The 1099-NEC worksheet (ef-047-5) — fetched only while its own tile is on screen, same
   * "not paid alongside the roster" reasoning `calendar`'s own effect above gives for the
   * filing calendar: every business gets an entity switch, most visits never open this
   * tile, and it is a vendor-side report with nothing in common with the payroll hooks
   * every other section on this screen already runs.
   */
  const [vendor1099, setVendor1099] = useState<Vendor1099Report | null>(null);
  const [vendor1099Status, setVendor1099Status] = useState<"idle" | "loading" | "ready" | "error">(
    "idle",
  );
  const [vendor1099Error, setVendor1099Error] = useState<string | null>(null);
  // ef-064-18 anchor: bumped by the "Missing tax info" list after a tax identity is saved; re-reads the worksheet WITHOUT the loading state, so
  // the list (and the dialog it opened) stay mounted while the server's answer arrives.
  const [vendor1099Reload, setVendor1099Reload] = useState(0);
  const reloadVendor1099 = useCallback(() => setVendor1099Reload((n) => n + 1), []);

  useEffect(() => {
    if (view !== "1099" || !entityId) return;
    const controller = new AbortController();
    if (vendor1099Reload === 0) setVendor1099Status("loading");
    demoApi
      .getVendor1099Report(entityId, TAX_YEAR, controller.signal)
      .then((report) => {
        setVendor1099(report);
        setVendor1099Status("ready");
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setVendor1099Error(problemMessage(err, "Could not build the 1099-NEC worksheet."));
        setVendor1099Status("error");
      });
    return () => controller.abort();
  }, [view, entityId, vendor1099Reload]);

  /**
   * The export is its own request, fired on the click — not fetched alongside the report,
   * same reasoning `Labour.tsx`'s identical export gives: paying for the whole CSV body on
   * every tile open would be wasted work for the visits that never press the button.
   */
  const [vendor1099Csv, setVendor1099Csv] = useState<
    | { kind: "idle" }
    | { kind: "busy" }
    | { kind: "done"; rows: number }
    | { kind: "error"; message: string }
  >({ kind: "idle" });

  async function downloadVendor1099Csv(): Promise<void> {
    if (!entityId) return;
    setVendor1099Csv({ kind: "busy" });
    try {
      const csv = await demoApi.getVendor1099Csv(entityId, TAX_YEAR);

      // The bytes are the server writer's output verbatim — same "add a blob and a
      // filename and nothing else" discipline Labour.tsx's identical download follows.
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `easyfarm-1099-nec-${TAX_YEAR}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      const rows = Math.max(csv.trim().split("\n").length - 1, 0);
      setVendor1099Csv({ kind: "done", rows });
    } catch (err) {
      setVendor1099Csv({
        kind: "error",
        message: problemMessage(err, "Could not build the 1099-NEC file."),
      });
    }
  }

  // Whether the SS cap actually bites on this roster — decides which explanation the
  // legend gives, so the screen never claims a distinction the reader cannot see.
  const allBelowCap =
    forms?.w2s.every((w) => w.ficaExempt || w.box3SocialSecurityWages === w.box5MedicareWages) ?? true;

  const nameFor = (employeeId: string) =>
    roster.employees?.find((e) => e.id === employeeId)?.name ?? employeeId;
  const numberFor = (employeeId: string) =>
    roster.employees?.find((e) => e.id === employeeId)?.number ?? "—";

  /* NAMES EVERY FORM BELOW IT, and no cadence.

     This line listed what the screen held when it was written and was never brought
     forward: the agricultural branch omitted the 940, and the non-agricultural branch
     omitted BOTH the W-2 table and the 940 — two of the three sections it sits above. A
     reader who trusts a subtitle stops scrolling at the last thing it names.

     The cadence word is gone on purpose rather than corrected. Once a 940 renders beside
     a 941 the page is annual AND quarterly at the same time, so no single word is true of
     it; each section states its own cadence in its own heading ("Form 941 — Q3 2026",
     "Form 940 — 2026 · due 31 January"), which is where a cadence belongs anyway.

     The state return is NAMED only when there is one. "the MN wage detail" was printed
     here regardless of where the business is — the same hardcode as the tile below, in
     the one line that tells a reader what this whole screen covers. With no program for
     their state the clause is dropped rather than replaced with a guess: the federal
     forms are still exactly what the page holds. */
  const subtitle = [
    isAgricultural ? "Forms 943, 940, W-2" : "Forms 941, 940, W-2",
    stateProgram ? `and the ${stateProgram.name} wage detail` : null,
    `· tax year ${TAX_YEAR}`,
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main className="year-end" data-state={status}>
      {/* PageHeader, not a hand-rolled <h1> — ADR 0009 §3's one-page-template rule
          applies here same as everywhere else this job touches. */}
      <PageHeader title="Payroll returns" subtitle={subtitle} />

      <AreaTabs area="/payroll" tabs={PAYROLL_TABS} label="Payroll" />

      {/*
        The two-business switcher, and the reason this screen is the demo's argument.
        Most farmers are not only farmers — they hold a job or run a second business —
        and that decides which return gets filed: an annual 943 for agricultural workers,
        a quarterly 941 for everyone else. One person, one login, both returns. A ledger
        that bills per company file cannot show this at all.
      */}
      {employers.length > 1 && (
        <nav className="year-end__switch" aria-label="Business">
          {employers.map((entity) => {
            const staff = rosterFor(entity.id) ?? [];
            const ag = staff.some((e) => e.ficaExemptByLaw);
            const selected = entity.id === entityId;
            return (
              <button
                key={entity.id}
                type="button"
                className="year-end__switch-btn"
                aria-current={selected ? "true" : undefined}
                onClick={() => setEntityId(entity.id)}
              >
                <span className="year-end__switch-name">{entity.name}</span>
                <span className="year-end__switch-meta">
                  {ag ? "Form 943 · annual" : "Form 941 · quarterly"} · {staff.length}{" "}
                  {staff.length === 1 ? "employee" : "employees"}
                </span>
              </button>
            );
          })}
        </nav>
      )}

      {/*
        The caveat leads. See the class remarks.

        ⚠️ IT MUST DESCRIBE THE RETURN THAT IS ON SCREEN, and for two years it described a
        different one. It read, in bold and above every number:

            "Projected, not filed. These are the ANNUAL forms ..."

        while four lines below it the Q1 card for the non-agricultural business read
        "Filed April 16 — $35,837.20 reported." Both visible at once, measured 2026-09-08
        by driving the screen. Two things wrong in one sentence: that return WAS filed, and
        a 941 is not an annual form.

        The subtitle immediately above this was corrected for the second of those already —
        "once a 940 renders beside a 941 the page is annual AND quarterly at the same time,
        so no single word is true of it." The fix was applied to the subtitle and not to the
        bolded sentence underneath it, which makes the same claim louder.

        What is ALWAYS true is the projection, so that leads and never varies. Whether a
        return has been filed is a property of the selected return, so it is read from the
        selected return rather than asserted over the whole screen. Christy reads this line
        aloud; a prospect who then reads on must not find it contradicted.
      */}
      <p className="year-end__basis" role="note">
        <strong>Projected figures.</strong>{" "}
        {shownReturnIsFiled
          ? "This quarter's return has already been filed — the card below says when, and what was sent can differ from this projection."
          : "Nothing on this screen has been filed."}
      </p>
      <Why>
        <p>
          Every figure is the payroll engine computing forward from the roster as it stands
          today, so this is the return as it would end up if the year runs at the current
          rate. It is not a year-to-date total.
        </p>
      </Why>

      {/*
        THE ROSTER'S OWN FAILURE, surfaced here.

        Every status branch on this screen keys off useYearEnd/useQuarterly/useAnnualFuta,
        and all three take the roster as INPUT. When the roster fetch fails they are never
        handed one, so none of them ever leaves "loading" — the screen sat on "Projecting
        the year…" indefinitely, with no error, for as long as anyone waited. Measured at
        2s, 8s and 15s: spinner still showing, error never shown.

        That is the worst shape a failure can take at a booth on venue wifi. Payroll and
        Paid Leave both say "Could not load the payroll roster." from the same hook; this
        screen was the one that said nothing, because it only ever asked its own
        downstream status whether anything was wrong.

        The spinners below are gated on this so a reader never sees "loading" and an error
        at the same time.
      */}
      {roster.status === "error" && (
        <StateMessage
          tone="error"
          title="Could not load the payroll roster."
          detail={roster.error ?? undefined}
        />
      )}

      {isAgricultural && roster.status !== "error" && status === "loading" && (
        <StateMessage tone="loading" title="Projecting the year…" />
      )}


      {isAgricultural && status === "error" && (
        <StateMessage
          tone="error"
          title="Could not project the year-end forms."
          detail={error ?? undefined}
        />
      )}

      {status === "ready" && !forms && (
        <StateMessage
          tone="empty"
          title="No employees on the roster."
          detail="Form 943 is filed by employers of agricultural labour. With nobody hired there is nothing to project."
        />
      )}

      {/* ---- The return selector: every form on this page is one click away ---- */}
      {status === "ready" && forms && (
        <nav className="year-end__quarters" aria-label="Return">
          {isAgricultural ? (
            <button
              type="button"
              className="year-end__quarter-btn"
              data-state="open"
              aria-current={view === "return" ? "true" : undefined}
              onClick={() => setView("return")}
            >
              <span className="year-end__quarter-label">Form 943</span>
              <span className="year-end__quarter-months">Annual</span>
              <span className="year-end__quarter-state">Agricultural</span>
            </button>
          ) : (
            [1, 2, 3, 4].map((q) => {
              const info = quarterOf(q);
              const today = calendar ? new Date(`${calendar.today}T00:00:00`) : null;
              const closed = info && today ? new Date(`${info.endDate}T00:00:00`) < today : false;
              const state = info?.filed ? "filed" : closed ? "unfiled" : "open";
              return (
                <button
                  key={q}
                  type="button"
                  className="year-end__quarter-btn"
                  data-state={state}
                  aria-current={view === "return" && q === quarter ? "true" : undefined}
                  onClick={() => {
                    setQuarter(q);
                    setView("return");
                  }}
                >
                  <span className="year-end__quarter-label">
                    Q{q}
                    {info?.filed && (
                      <span className="year-end__tick" aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </span>
                  <span className="year-end__quarter-months">
                    {["Jan–Mar", "Apr–Jun", "Jul–Sep", "Oct–Dec"][q - 1]}
                  </span>
                  <span className="year-end__quarter-state">
                    {info?.filed ? "Filed" : closed ? "Not filed" : "Open"}
                  </span>
                </button>
              );
            })
          )}

          {/* Both businesses file a 940, and every employee gets a W-2. Same tile shape as
              the quarters so the row reads as one selector rather than two ideas. */}
          <button
            type="button"
            className="year-end__quarter-btn"
            data-state="open"
            aria-current={view === "940" ? "true" : undefined}
            onClick={() => setView("940")}
          >
            <span className="year-end__quarter-label">Form 940</span>
            <span className="year-end__quarter-months">Annual</span>
            <span className="year-end__quarter-state">Due 31 Jan</span>
          </button>

          <button
            type="button"
            className="year-end__quarter-btn"
            data-state="open"
            aria-current={view === "w2" ? "true" : undefined}
            onClick={() => setView("w2")}
          >
            <span className="year-end__quarter-label">W-2</span>
            <span className="year-end__quarter-months">Annual</span>
            <span className="year-end__quarter-state">Per employee</span>
          </button>

          {/* The fourth annual tile — 1099-NEC (ef-047-5). EVERY business can owe one, same
              "not gated on the 943/941 split" reasoning the 940 tile's own comment gives:
              it takes whichever business is selected, not just the agricultural one. */}
          <button
            type="button"
            className="year-end__quarter-btn"
            data-state="open"
            aria-current={view === "1099" ? "true" : undefined}
            onClick={() => setView("1099")}
          >
            <span className="year-end__quarter-label">1099-NEC</span>
            <span className="year-end__quarter-months">Annual</span>
            <span className="year-end__quarter-state">Per vendor</span>
          </button>

          {/* The STATE return, sitting with the federal ones.
          
              MN Paid Leave had its own destination, its own card in the Payroll sidebar and
              its own button on the dashboard — a quarterly wage detail advertised harder than
              the 941 it sits beside. It is a payroll tax whose filing is a return like the
              others, so it belongs in this row: one selector for "what do I have to file",
              federal and state together, rather than a section of its own outside them.
          
              It NAVIGATES where its neighbours switch a view, because the wage detail carries
              editable program parameters and a downloadable file and is a screen in its own
              right. Marked with the arrow so the tile does not promise an in-place swap. */}
          {/* THE LOOKUP FAILED — said, not hidden. Rendering nothing here would be
              indistinguishable from a state we do not cover, and for a Minnesota business
              that is a wrong answer produced by a network error. Loading renders nothing on
              purpose: it resolves in well under a second and a flash of "could not check"
              would be the wrong thing to read on the way to the right one. */}
          {onNavigate && statePlStatus === "error" && (
            <span
              className="year-end__quarter-btn year-end__quarter-btn--none"
              data-state="error"
              data-state-program="unknown"
            >
              <span className="year-end__quarter-label">State paid leave</span>
              <span className="year-end__quarter-months">Could not check</span>
              <span className="year-end__quarter-state">Try reloading</span>
            </span>
          )}

          {onNavigate && stateProgram !== undefined && (
            stateProgram ? (
              <button
                type="button"
                className="year-end__quarter-btn"
                data-state="open"
                data-state-program={stateProgram.state}
                onClick={() => onNavigate("/paid-leave")}
              >
                <span className="year-end__quarter-label">
                  {stateProgram.name}<span aria-hidden="true"> →</span>
                </span>
                <span className="year-end__quarter-months">Quarterly</span>
                <span className="year-end__quarter-state">Wage detail</span>
              </button>
            ) : (
              /*
                NO PROGRAM FOR THIS BUSINESS'S STATE — rendered, not hidden.

                Absence is the finding, the same way "Not signed" is on the approval queue. A
                farmer in a state this build does not cover needs to see that the row is
                complete as far as EasyFarm is concerned; a tile that simply vanished would
                leave them wondering whether a state return exists and was missed.

                ⚠️ THE WORDING CLAIMS NOTHING ABOUT THE LAW. "EasyFarm has no state paid-leave
                return for ND" is a fact about this product. "ND has no paid-leave programme"
                is a legal claim nobody here has made, and the catalog cannot tell the two
                apart — it throws identically for a state with no programme and one whose
                rates nobody has entered yet.
              */
              <span
                className="year-end__quarter-btn year-end__quarter-btn--none"
                data-state="unavailable"
                data-state-program="none"
              >
                <span className="year-end__quarter-label">State paid leave</span>
                <span className="year-end__quarter-months">
                  {selectedEntity?.state
                    ? `None in EasyFarm for ${selectedEntity.state}`
                    : "No state set for this business"}
                </span>
                {/* No third line. Its neighbours use it for a qualifier — AGRICULTURAL,
                    DUE 31 JAN, WAGE DETAIL — and the first attempt here read "Not a filing
                    status", which is both cryptic and one careless reading away from
                    sounding like a claim about whether the business owes anything. The two
                    lines above already say the whole of what is known. */}
              </span>
            )
          )}
        </nav>
      )}

      {/* ---- Form 941: the non-agricultural, quarterly return ---- */}
      {!isAgricultural && view === "return" && (
        <>
          {/*
            The whole point of the filing table, stated in one sentence a person who does
            not enjoy bookwork can act on: which return is next, when it is due, and how
            long they have. Everything here is derived from real filing rows and the
            SERVER's date — an unfiled quarter past its deadline says "overdue", because
            a screen that softened that would be the one thing worse than no screen.
          */}
          {nextDue && calendar && (() => {
            const today = new Date(`${calendar.today}T00:00:00`);
            const due = new Date(`${nextDue.dueDate}T00:00:00`);
            const closes = new Date(`${nextDue.endDate}T00:00:00`);
            const days = Math.round((due.getTime() - today.getTime()) / 86_400_000);
            const stillOpen = closes >= today;
            const overdue = days < 0;
            return (
              <p
                className="year-end__due"
                data-tone={overdue ? "overdue" : days <= 14 ? "soon" : "calm"}
                role="note"
              >
                <strong>
                  {overdue
                    ? `Q${nextDue.quarter} is overdue by ${Math.abs(days)} days.`
                    : `Q${nextDue.quarter} is next.`}
                </strong>{" "}
                {stillOpen
                  ? `The quarter closes ${formatDay(nextDue.endDate)} and the return is due ${formatDay(nextDue.dueDate)} — ${days} days from now. Nothing to send yet.`
                  : overdue
                    ? `It closed ${formatDay(nextDue.endDate)} and was due ${formatDay(nextDue.dueDate)}. File it as soon as you can.`
                    : `The quarter has closed. The return is due ${formatDay(nextDue.dueDate)} — ${days} days from now.`}
              </p>
            );
          })()}


          {roster.status !== "error" && quarterly.status === "loading" && (
            <StateMessage tone="loading" title="Projecting the quarter…" />
          )}

          {quarterly.status === "error" && (
            <StateMessage
              tone="error"
              title="Could not project the quarterly return."
              detail={quarterly.error ?? undefined}
            />
          )}

          {quarterly.status === "ready" && quarterly.form && (
            <section className="card year-end__form941" aria-labelledby="form941-heading">
              <h2 id="form941-heading" className="year-end__card-title">
                Form 941 — Q{quarterly.form.quarter} {quarterly.form.taxYear}
                <span className="year-end__card-sub">
                  Employer&apos;s Quarterly Federal Tax Return ·{" "}
                  {quarterOf(quarter)?.payPeriods ?? "—"} paydays in this quarter
                </span>
              </h2>

              {/* A filed quarter shows what was FILED, not what today's roster would
                  produce. A return says what it said on the day it was sent. */}
              {quarterOf(quarter)?.filed && (
                <>
                  <p className="year-end__filed" role="note">
                    <strong>Filed {formatDay(quarterOf(quarter)!.filedOn!)}</strong> —{" "}
                    {formatMoney(quarterOf(quarter)!.totalTaxes!)} reported.
                  </p>
                  <Why>
                    <p>
                      The figures below are recomputed from today&apos;s roster, so they can
                      differ from what was sent if the roster has changed since.
                    </p>
                  </Why>
                </>
              )}

              {/*
                Numbered as the actual return numbers them — verified against the IRS
                instructions for Form 941, like the 943 beside it. Note the shape is NOT
                the 943's: the 941 carries a Line 2 for gross wages, splits the FICA lines
                into 5a/5c/5d, subtotals them at 5e, and lands on Line 6. Same statutory
                rates, different return.
              */}
              <dl className="year-end__lines">
                <div className="year-end__line">
                  <dt><span className="year-end__lineno">1</span>Employees this quarter</dt>
                  <dd className="count">{quarterly.form.form941.employeeCount}</dd>
                </div>
                <div className="year-end__line">
                  <dt><span className="year-end__lineno">2</span>Wages, tips and other compensation</dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.wagesTipsOtherCompensation)}</dd>
                </div>
                <div className="year-end__line">
                  <dt><span className="year-end__lineno">3</span>Federal income tax withheld</dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.federalIncomeTaxWithheld)}</dd>
                </div>
                <div className="year-end__line">
                  <dt><span className="year-end__lineno">5a</span>Taxable Social Security wages</dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.socialSecurityWages)}</dd>
                </div>
                <div className="year-end__line year-end__line--sub">
                  <dt>
                    <span className="year-end__lineno" />
                    Social Security tax
                    <span className="year-end__rate">
                      {(quarterly.form.form941.socialSecurityCombinedRate * 100).toFixed(1)}%
                    </span>
                  </dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.socialSecurityTax)}</dd>
                </div>
                <div className="year-end__line">
                  <dt><span className="year-end__lineno">5c</span>Taxable Medicare wages and tips</dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.medicareWages)}</dd>
                </div>
                <div className="year-end__line year-end__line--sub">
                  <dt>
                    <span className="year-end__lineno" />
                    Medicare tax
                    <span className="year-end__rate">
                      {(quarterly.form.form941.medicareCombinedRate * 100).toFixed(1)}%
                    </span>
                  </dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.medicareTax)}</dd>
                </div>
                <div className="year-end__line">
                  <dt><span className="year-end__lineno">5d</span>Wages subject to Additional Medicare</dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.additionalMedicareWages)}</dd>
                </div>
                <div className="year-end__line">
                  <dt><span className="year-end__lineno">5e</span>Total Social Security and Medicare</dt>
                  <dd className="amount">
                    {formatMoney(quarterly.form.form941.totalSocialSecurityAndMedicareTaxes)}
                  </dd>
                </div>
                <div className="year-end__line year-end__line--total">
                  <dt><span className="year-end__lineno">6</span>Total taxes before adjustments</dt>
                  <dd className="amount">{formatMoney(quarterly.form.form941.totalTaxesBeforeAdjustments)}</dd>
                </div>
              </dl>

              {/* The wrong-roster guard. See Form941Lines.UnexpectedFicaExemptCount. */}
              {quarterly.form.form941.unexpectedFicaExemptCount > 0 && (
                <>
                  <p className="year-end__h2a">
                    <strong>
                      {quarterly.form.form941.unexpectedFicaExemptCount} H-2A worker(s) on
                      this roster.
                    </strong>
                  </p>
                  <Why>
                    <p>
                      The H-2A exemption is agricultural, so those wages belong on an annual
                      Form 943, not here. Their FICA wages are excluded from the lines above
                      rather than silently taxed — but the roster itself needs checking.
                    </p>
                  </Why>
                </>
              )}

              <p className="year-end__legend">
                Filed four times a year — Q{quarterly.form.quarter} is due{" "}
                {["30 April", "31 July", "31 October", "31 January"][quarterly.form.quarter - 1]}.
              </p>
              <Why>
                <p>
                  Line 5b (Social Security tips) and 5f are not shown: nothing here records
                  tips, and an invented zero on a return reads as &ldquo;we checked&rdquo;.
                </p>
              </Why>
            </section>
          )}
        </>
      )}

      {status === "ready" && forms && isAgricultural && view === "return" && (
        <div className="year-end__grid year-end__grid--w2only">
          {true && (
          <section className="card year-end__form943" aria-labelledby="form943-heading">
            <h2 id="form943-heading" className="year-end__card-title">
              Form 943
              <span className="year-end__card-sub">
                Employer&apos;s Annual Federal Tax Return for Agricultural Employees
              </span>
            </h2>

            {/*
              Numbered as the actual return numbers them — verified against the IRS
              instructions for Form 943, not recalled. Line 1 is the employee count, 2/3
              Social Security wages and tax, 4/5 Medicare, 6/7 Additional Medicare, 8
              federal income tax withheld, 9 the total before adjustments. Someone
              transcribing onto the real form reads down the same numbers in the same
              order, which is the entire reason this screen is laid out as a form rather
              than as a set of dashboard cards.
            */}
            <dl className="year-end__lines">
              <div className="year-end__line">
                <dt><span className="year-end__lineno">1</span>Agricultural employees</dt>
                <dd className="count">{forms.w2s.length}</dd>
              </div>
              <div className="year-end__line">
                <dt><span className="year-end__lineno">2</span>Wages subject to Social Security tax</dt>
                <dd className="amount">{formatMoney(forms.form943.socialSecurityWages)}</dd>
              </div>
              <div className="year-end__line">
                <dt>
                  <span className="year-end__lineno">3</span>Social Security tax
                  <span className="year-end__rate">
                    {(forms.form943.socialSecurityCombinedRate * 100).toFixed(1)}%
                  </span>
                </dt>
                <dd className="amount">{formatMoney(forms.form943.socialSecurityTax)}</dd>
              </div>
              <div className="year-end__line">
                <dt><span className="year-end__lineno">4</span>Wages subject to Medicare tax</dt>
                <dd className="amount">{formatMoney(forms.form943.medicareWages)}</dd>
              </div>
              <div className="year-end__line">
                <dt>
                  <span className="year-end__lineno">5</span>Medicare tax
                  <span className="year-end__rate">
                    {(forms.form943.medicareCombinedRate * 100).toFixed(1)}%
                  </span>
                </dt>
                <dd className="amount">{formatMoney(forms.form943.medicareTax)}</dd>
              </div>
              <div className="year-end__line">
                <dt>
                  <span className="year-end__lineno">6</span>Wages subject to Additional Medicare
                </dt>
                <dd className="amount">{formatMoney(forms.form943.additionalMedicareWages)}</dd>
              </div>
              <div className="year-end__line">
                <dt><span className="year-end__lineno">7</span>Additional Medicare withholding</dt>
                <dd className="amount">{formatMoney(forms.form943.additionalMedicareTax)}</dd>
              </div>
              <div className="year-end__line">
                <dt><span className="year-end__lineno">8</span>Federal income tax withheld</dt>
                <dd className="amount">{formatMoney(forms.form943.federalIncomeTaxWithheld)}</dd>
              </div>
              <div className="year-end__line year-end__line--total">
                <dt><span className="year-end__lineno">9</span>Total taxes before adjustments</dt>
                <dd className="amount">{formatMoney(forms.form943.totalTaxes)}</dd>
              </div>
            </dl>

            {/*
              The farm-specific fact this whole screen exists to state. Without it a reader
              comparing the 943 wages to the payroll total sees a shortfall and assumes a bug.
            */}
            {forms.form943.ficaExemptEmployeeCount > 0 && (
              <>
                <p className="year-end__h2a" role="note" aria-label="H-2A exemption">
                  <strong>
                    {forms.form943.ficaExemptEmployeeCount} H-2A{" "}
                    {forms.form943.ficaExemptEmployeeCount === 1 ? "worker is" : "workers are"}{" "}
                    excluded
                  </strong>{" "}
                  from the wage lines above.
                </p>
                <Why>
                  <p>
                    H-2A agricultural wages are not Social Security or Medicare wages at all,
                    so they are not reportable there — not merely untaxed. Their federal
                    income tax withheld <em>is</em> included, because the exemption does not
                    extend to income-tax withholding.
                  </p>
                </Why>
              </>
            )}
          </section>
          )}
        </div>
      )}

      {status === "ready" && forms && view === "w2" && (
        <div className="year-end__grid year-end__grid--w2only">
          <section className="card year-end__w2s" aria-labelledby="w2-heading-title">
            {/*
              The section's own accessible name is scoped to just the title text
              (`w2-heading-title`) rather than the whole heading — the sub-line below
              is a description, not part of the name, and including it collided with
              the "W-2 wage and tax boxes" exact name this landmark is looked up by
              (this file's own axe pass and `getByRole` locators elsewhere).
            */}
            <h2 id="w2-heading" className="year-end__card-title">
              <span id="w2-heading-title">W-2 wage and tax boxes</span>
              <span className="year-end__card-sub">One row per employee</span>
            </h2>

            {/*
              tabIndex + role/name: a horizontally scrollable region must be reachable and
              scrollable by KEYBOARD, or a keyboard-only user simply cannot see the
              right-hand columns — here, every figure after the employee's name.

              PaidLeave.tsx carries this same three-line fix with the same comment, and it
              was never applied here. Measured 2026-09-08 the moment /year-end got an axe
              test of its own:

                  scrollable-region-focusable (serious): Scrollable region must have
                  keyboard access

              PHONE ONLY, which is why it survived. At 1280px this table fits and the
              wrapper does not scroll, so axe has nothing to flag; at 390px it is 640px wide
              and it does. A desktop-only scan reports a clean bill.
            */}
            <div
              className="year-end__scroll"
              tabIndex={0}
              role="region"
              aria-label="W-2 table, scroll for more columns"
            >
              <table className="year-end__table ruled-rows">
                <caption className="visually-hidden">
                  Projected W-2 boxes 1 to 6 for each employee, tax year {TAX_YEAR}
                </caption>
                <thead>
                  {/*
                    Number then name, one line each — the way a real W-2 prints its own
                    boxes ("1 Wages, tips, other compensation"). The previous version
                    stacked the name over the box reference and ran to three lines and
                    101px of header, which is not a shape any financial product has.
                  */}
                  <tr>
                    <th scope="col">Employee</th>
                    <th scope="col" className="num">
                      <span className="year-end__lineno">1</span>Gross wages
                    </th>
                    <th scope="col" className="num">
                      <span className="year-end__lineno">2</span>Federal tax
                    </th>
                    <th scope="col" className="num">
                      <span className="year-end__lineno">3</span>Soc. Sec. wages
                    </th>
                    <th scope="col" className="num">
                      <span className="year-end__lineno">5</span>Medicare wages
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {forms.w2s.map((w2) => (
                    <tr key={w2.employeeId}>
                      <th scope="row">
                        <span className="year-end__emp-num">{numberFor(w2.employeeId)}</span>
                        {nameFor(w2.employeeId)}
                        {w2.ficaExempt && <span className="year-end__badge">H-2A</span>}
                      </th>
                      <td className="amount">{formatMoney(w2.box1FederalWages)}</td>
                      <td className="amount">{formatMoney(w2.box2FederalTax)}</td>
                      <td className="amount">
                        {formatMoney(w2.box3SocialSecurityWages)}
                        {/* A blank Box 3 on an H-2A W-2 is correct and reads as a bug
                            without this. */}
                        {w2.ficaExempt && <span className="year-end__zero-why">exempt</span>}
                      </td>
                      <td className="amount">{formatMoney(w2.box5MedicareWages)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/*
              Answers "why are these three columns the same number?" before it is asked.
              They are separate boxes that diverge for two reasons — a pretax election
              (which lowers Box 1 below Boxes 3 and 5) and the Social Security cap (which
              holds Box 3 while Box 5 keeps climbing). This roster triggers neither, so
              they match, and matching columns read as padding unless the screen says why.
            */}
            <p className="year-end__legend">
              <strong>Social Security wages stop at ${SS_WAGE_BASE_LABEL}</strong> for{" "}
              {TAX_YEAR}; Medicare has no cap.
            </p>
            <Why>
              <p>
                {allBelowCap
                  ? "Nobody on this roster reaches that ceiling, and none of them has a pretax deduction, so all three wage columns match. They separate as soon as either changes."
                  : "Employees at or above the ceiling show a Social Security figure lower than their Medicare figure."}
              </p>
            </Why>

            {forms.excluded.length > 0 && (
              <StateMessage
                tone="error"
                title={`${forms.excluded.length} employee(s) could not be projected.`}
                detail="They are excluded from the Form 943 totals above, which are a real subtotal of the rest of the roster."
              />
            )}
          </section>
        </div>
      )}
      {/*
        ORDER MATTERS: the 940 comes AFTER whichever primary return is showing, not
        before it. Placed first it pushed the 943 and the W-2 table below the fold, so
        the screen opened on the SMALLEST of the three figures — $336 of unemployment
        tax above a $94,876 federal return. The primary return is what the reader came
        for; the 940 is the annual footnote both businesses also owe.
      */}
      {/* ---- Form 940: the annual unemployment return BOTH businesses file ---- */}
      {futa.status === "ready" && futa.form && view === "940" && (
        <section className="card year-end__form940" aria-labelledby="form940-heading">
          <h2 id="form940-heading" className="year-end__card-title">
            Form 940 — {futa.form.taxYear}
            <span className="year-end__card-sub">
              Employer&apos;s Annual Federal Unemployment (FUTA) Tax Return · due 31 January
            </span>
          </h2>

          {/*
            Numbered as the return numbers them — verified against the IRS instructions,
            like the 943 and 941. Part 2 only: lines 3 to 8. Part 3's credit-reduction
            adjustments need per-state unemployment data that does not exist anywhere in
            this system, and are absent rather than zeroed, because a zero on a return
            reads as "we checked".
          */}
          <dl className="year-end__lines">
            <div className="year-end__line">
              <dt><span className="year-end__lineno">3</span>Total payments to all employees</dt>
              <dd className="amount">{formatMoney(futa.form.form940.totalPayments)}</dd>
            </div>
            <div className="year-end__line">
              <dt><span className="year-end__lineno">4</span>Payments exempt from FUTA tax</dt>
              <dd className="amount">{formatMoney(futa.form.form940.paymentsExemptFromFuta)}</dd>
            </div>
            <div className="year-end__line">
              <dt>
                <span className="year-end__lineno">5</span>Payments over ${FUTA_WAGE_BASE_LABEL} per employee
              </dt>
              <dd className="amount">{formatMoney(futa.form.form940.paymentsExcessOfWageBase)}</dd>
            </div>
            <div className="year-end__line">
              <dt><span className="year-end__lineno">6</span>Subtotal</dt>
              <dd className="amount">{formatMoney(futa.form.form940.subtotal)}</dd>
            </div>
            <div className="year-end__line">
              <dt><span className="year-end__lineno">7</span>Total taxable FUTA wages</dt>
              <dd className="amount">{formatMoney(futa.form.form940.totalTaxableFutaWages)}</dd>
            </div>
            <div className="year-end__line year-end__line--total">
              <dt>
                <span className="year-end__lineno">8</span>FUTA tax before adjustments
                <span className="year-end__rate">
                  {(futa.form.form940.effectiveRate * 100).toFixed(1)}%
                </span>
              </dt>
              <dd className="amount">{formatMoney(futa.form.form940.futaTaxBeforeAdjustments)}</dd>
            </div>
          </dl>

          <p className="year-end__h2a">
            Line 8 is <strong>{(futa.form.form940.effectiveRate * 100).toFixed(1)}%</strong>{" "}
            of taxable FUTA wages.
          </p>
          <Why>
            <p>
              The rate is {(futa.form.form940.grossRate * 100).toFixed(1)}% less the maximum
              state-unemployment credit of {(futa.form.form940.maxCredit * 100).toFixed(1)}%,
              so line 8 is the {(futa.form.form940.effectiveRate * 100).toFixed(1)}% most
              employers pay — and only on the first ${FUTA_WAGE_BASE_LABEL} of each
              person&apos;s wages, which is why line 5 is usually the largest figure here.
              {futa.form.form940.futaExemptEmployeeCount > 0 && (
                <>
                  {" "}
                  {/*
                    THE VERB HAS TO AGREE TOO, and it did not.

                    This pluralised the NOUN and left "sit" fixed, so at one exempt worker the
                    screen read "1 H-2A worker SIT on line 4". Measured by rendering it —
                    every demo tenant seeds FOUR H-2A workers, so the singular branch of this
                    sentence had never been on screen and could not be found by looking at the
                    demo. Its sibling two hundred lines up, the 943's own exemption note, had
                    it right all along: "worker is" / "workers are".

                    This is the Form 940 explanation Christy reads out. A farmer with one H-2A
                    worker is an ordinary customer, not an edge case.
                  */}
                  <strong>
                    {futa.form.form940.futaExemptEmployeeCount} H-2A worker
                    {futa.form.form940.futaExemptEmployeeCount === 1 ? "" : "s"}
                  </strong>{" "}
                  {futa.form.form940.futaExemptEmployeeCount === 1 ? "sits" : "sit"} on line 4:
                  H-2A wages are exempt from FUTA as well as from FICA — two
                  separate exemptions that happen to cover the same people.
                </>
              )}
            </p>
          </Why>
        </section>
      )}

      {/* ---- 1099-NEC: the vendor-side annual worksheet (ef-047-5) ---- */}
      {view === "1099" && (
        <section className="card year-end__vendor1099" aria-labelledby="vendor1099-heading">
          <h2 id="vendor1099-heading" className="year-end__card-title">
            1099-NEC worksheet
            <span className="year-end__card-sub">
              Nonemployee compensation, per vendor · tax year {TAX_YEAR}
            </span>
          </h2>

          <p className="year-end__h2a" role="note">
            Review each vendor&apos;s tax info before filing; this worksheet is not a filed 1099-NEC.
          </p>

          {vendor1099Status === "loading" && (
            <StateMessage tone="loading" title="Building the worksheet…" />
          )}

          {vendor1099Status === "error" && (
            <StateMessage
              tone="error"
              title="Could not build the 1099-NEC worksheet."
              detail={vendor1099Error ?? undefined}
            />
          )}

          {vendor1099Status === "ready" && vendor1099 && (
            <>
              {vendor1099.vendors.length === 0 ? (
                <StateMessage
                  tone="empty"
                  title="No vendor crossed $600 this year."
                  detail="Vendors paid under the IRS reporting threshold are not listed."
                />
              ) : (
                <div
                  className="year-end__scroll"
                  tabIndex={0}
                  role="region"
                  aria-label="1099-NEC table, scroll for more columns"
                >
                  <table className="year-end__table ruled-rows">
                    <caption className="visually-hidden">
                      Vendors paid $600 or more in {TAX_YEAR}, cash basis
                    </caption>
                    <thead>
                      <tr>
                        <th scope="col">Vendor</th>
                        <th scope="col">Tax ID</th>
                        <th scope="col" className="num">Settled (bills)</th>
                        <th scope="col" className="num">Direct</th>
                        <th scope="col" className="num">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {vendor1099.vendors.map((v) => (
                        <tr key={v.vendorId}>
                          <th scope="row">{v.name}</th>
                          <td>{v.tinMasked ?? "Missing"}</td>
                          <td className="amount">{formatMoney(v.settledTotal)}</td>
                          <td className="amount">{formatMoney(v.directTotal)}</td>
                          <td className="amount">{formatMoney(v.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* ef-064-18 anchor - the "Missing tax info" list (ef-064-18 REPLACES the component; this line stays). */}
              <MissingTaxInfo report={vendor1099} onChanged={reloadVendor1099} />

              {vendor1099.belowThresholdCount > 0 && (
                <p className="year-end__legend">
                  {vendor1099.belowThresholdCount} more{" "}
                  {vendor1099.belowThresholdCount === 1 ? "vendor" : "vendors"} paid under
                  $600 and {vendor1099.belowThresholdCount === 1 ? "is" : "are"} excluded —
                  below the IRS reporting threshold for a 1099-NEC.
                </p>
              )}

              <button
                type="button"
                className="btn btn--ghost year-end__csv-btn"
                disabled={vendor1099Csv.kind === "busy"}
                onClick={() => void downloadVendor1099Csv()}
              >
                {vendor1099Csv.kind === "busy" ? "Building the file…" : "Download CSV"}
              </button>

              {vendor1099Csv.kind === "done" && (
                <p className="year-end__legend" role="status">
                  {vendor1099Csv.rows === 0
                    ? "Nothing to export — the file has its column headings and nothing else."
                    : `${vendor1099Csv.rows} ${vendor1099Csv.rows === 1 ? "vendor" : "vendors"} saved to the CSV file.`}
                </p>
              )}
              {vendor1099Csv.kind === "error" && (
                <p className="year-end__legend" role="alert">
                  {vendor1099Csv.message} Nothing was downloaded.
                </p>
              )}
            </>
          )}
        </section>
      )}

    </main>
  );
}
